<!--
  The toolbar fragment — the search field, the filter section's toggle and the out-of-profile count — is rendered by
  the board's header row through `toolbar` (`kanban.component.html`): the bar below is a
  full-width row of its own, and both read the one filter state this component keeps.
-->
<ng-template #toolbar>
  <div class="input-trend-search me-2">
    <input
      type="search"
      class="form-control"
      [placeholder]="'kanban.filterPanel.searchPlaceholder' | translate"
      [formControl]="searchControl"
    />
    <span class="mdi mdi-magnify search-icon"></span>
  </div>

  <button
    type="button"
    class="btn trend-button d-inline-flex align-items-center gap-1"
    [class.btn-primary]="sectionOpen()"
    [class.btn-outline-secondary]="!sectionOpen()"
    [attr.aria-expanded]="sectionOpen()"
    aria-controls="kanban-filter-section"
    [attr.aria-label]="sectionToggleLabel() | translate"
    [matTooltip]="sectionToggleLabel() | translate"
    (click)="toggleSection()"
  >
    <i class="mdi mdi-filter-variant"></i>
    <!-- The label gives way below 1200px, leaving the icon, the count and the chevron. -->
    <span class="d-none d-xl-inline">{{ "kanban.filterPanel.filters" | translate }}</span>
    @if (activeCount() > 0) {
      <span
        class="badge rounded-pill"
        [class.bg-primary]="!sectionOpen()"
        [class.bg-white]="sectionOpen()"
        [class.text-primary]="sectionOpen()"
      >
        {{ activeCount() }}
      </span>
    }
    <i class="mdi" [class.mdi-chevron-down]="!sectionOpen()" [class.mdi-chevron-up]="sectionOpen()"></i>
  </button>
  @if ((outOfIcpCount() !== null && outOfIcpCount() > 0) || isOutOfIcpOnly()) {
    <button
      type="button"
      class="btn trend-button me-2 d-inline-flex align-items-center"
      [class.btn-warning]="isOutOfIcpOnly()"
      [class.btn-soft-warning]="!isOutOfIcpOnly()"
      [attr.aria-pressed]="isOutOfIcpOnly()"
      [matTooltip]="'kanban.filters.outOfIcpCountTooltip' | translate"
      (click)="toggleOutOfIcpOnly()"
    >
      <i class="mdi mdi-account-off-outline me-1"></i>
      <span class="font-12 fw-semibold">
        {{
          "kanban.filters.outOfIcpCount"
            | translate: { count: outOfIcpCount() ?? 0 }
        }}
      </span>
    </button>
  }
</ng-template>

<!--
  One chip, applied or still waiting for a value, with its editor below it. Rendered twice
  through this template — on the line and inside the "+N" popover — so both places edit
  and remove a filter the same way.
-->
<ng-template #chipTemplate let-chip let-inMore="inMore">
  <div class="filter-chip" [class.open]="openKey() === chip.key">
    <button
      type="button"
      class="filter-chip-main"
      [attr.aria-expanded]="openKey() === chip.key"
      [title]="chip.title"
      (click)="toggleChip(chip.key, inMore)"
    >
      <span class="fw-bold">{{ chip.label }}</span>
      @if (chip.verb) {
        <span>{{ chip.verb }}</span>
      }
      <span class="filter-chip-value" [class.text-muted]="!chip.active">{{
        chip.value
      }}</span>
      <i class="mdi mdi-chevron-down"></i>
    </button>
    <button
      type="button"
      class="filter-chip-remove"
      [attr.aria-label]="
        'kanban.filterPanel.removeFilterNamed'
          | translate: { label: chip.label }
      "
      (click)="clearFilter(chip.key)"
    >
      <i class="mdi mdi-close"></i>
    </button>
  </div>

  @if (openKey() === chip.key) {
    <div
      class="shadow filter-menu filter-menu-editor"
      [class.filter-menu-date]="chip.def.type === 'daterange'"
    >
      @switch (chip.def.type) {
        @case ("daterange") {
          <!--
            The platform calendar, inline, with the presets, OK and Clear the range picker
            offers in its footer. It unfolds along with the chip; the calendar button folds it.
          -->
          <div class="p-2">
            <div class="filter-date-field">
              <input
                type="text"
                class="filter-date-input"
                readonly
                [placeholder]="'kanban.filterPanel.startDate' | translate"
                [attr.aria-label]="'kanban.filterPanel.startDate' | translate"
                [value]="(dateDraft(chip.key).start | date: 'dd/MM/yyyy') ?? ''"
                (click)="toggleCalendar(chip.key)"
              />
              <span class="text-muted px-1">–</span>
              <input
                type="text"
                class="filter-date-input"
                readonly
                [placeholder]="'kanban.filterPanel.endDate' | translate"
                [attr.aria-label]="'kanban.filterPanel.endDate' | translate"
                [value]="(dateDraft(chip.key).end | date: 'dd/MM/yyyy') ?? ''"
                (click)="toggleCalendar(chip.key)"
              />
              <button
                type="button"
                class="btn btn-primary filter-date-toggle"
                [attr.aria-expanded]="calendarKey() === chip.key"
                [attr.aria-label]="
                  'kanban.filterPanel.calendarToggle' | translate
                "
                (click)="toggleCalendar(chip.key)"
              >
                <i class="mdi mdi-calendar-blank"></i>
              </button>
            </div>

            @if (calendarKey() === chip.key) {
              <mat-calendar
                class="filter-date-calendar mt-2"
                [selected]="dateDraft(chip.key)"
                [startAt]="dateDraft(chip.key).start"
                (selectedChange)="pickCalendarDate(chip.key, $event)"
              />
              <div class="d-flex align-items-center gap-2 pt-2 mt-1 border-top">
                <mat-select
                  class="input-field rounded-lg flex-grow-1"
                  [placeholder]="
                    'kanban.filterPanel.selectPlaceholder' | translate
                  "
                  (selectionChange)="onDatePreset(chip.key, $event)"
                >
                  <mat-option value="1">{{
                    "kanban.filterPanel.today" | translate
                  }}</mat-option>
                  <mat-option value="7">{{
                    "kanban.filterPanel.last7Days" | translate
                  }}</mat-option>
                  <mat-option value="15">{{
                    "kanban.filterPanel.last15Days" | translate
                  }}</mat-option>
                  <mat-option value="30">{{
                    "kanban.filterPanel.last30Days" | translate
                  }}</mat-option>
                  <mat-option value="60">{{
                    "kanban.filterPanel.last60Days" | translate
                  }}</mat-option>
                </mat-select>
                <button
                  type="button"
                  class="btn btn-sm btn-primary"
                  [disabled]="!canApplyDate(chip.key)"
                  (click)="applyDateRange(chip.key)"
                >
                  {{ "kanban.filterPanel.ok" | translate }}
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-link text-muted text-decoration-none"
                  (click)="clearDateDraft(chip.key)"
                >
                  {{ "kanban.filterPanel.clear" | translate }}
                </button>
              </div>
            }
          </div>
        }
        @case ("number") {
          <!-- A score bound (RF-16). Score is 0–100 by definition, so the input is bounded rather than free. -->
          <div class="p-2">
            <label
              class="form-label font-12 text-muted mb-1"
              [for]="'filter-number-' + chip.key"
            >
              {{
                (chip.key === "scoreMin"
                  ? "kanban.filterPanel.scoreMinHint"
                  : "kanban.filterPanel.scoreMaxHint"
                ) | translate
              }}
            </label>
            <input
              [id]="'filter-number-' + chip.key"
              type="number"
              class="form-control"
              min="0"
              max="100"
              step="1"
              [value]="draftNumber()"
              (input)="onNumberChange($any($event.target).value)"
            />
          </div>
        }
        @default {
          <!-- One list for every option-based filter, fixed values and loaded entities alike. -->
          @if (hasSearch(chip.def)) {
            <div class="input-trend-search px-2 pt-1">
              <input
                type="text"
                class="form-control"
                [formControl]="listSearch"
                [placeholder]="'kanban.filterPanel.searchOptions' | translate"
              />
              <span class="mdi mdi-magnify search-icon"></span>
            </div>
          }
          <div class="list-group list-group-flush filter-menu-scroll">
            @for (option of menuOptions(); track option.value) {
              <a
                href="javascript:void(0);"
                class="list-group-item list-group-item-action p-2 font-12"
                (click)="pickOption(chip.def, option.value)"
              >
                <div class="d-flex align-items-center justify-content-between">
                  <span class="text-truncate">{{ option.label }}</span>
                  @if (isSelected(chip.def, option.value)) {
                    <i class="mdi mdi-check text-primary fs-4 ms-2"></i>
                  }
                </div>
              </a>
            } @empty {
              <div class="p-2 font-12 text-muted">
                {{ "kanban.filterPanel.noResults" | translate }}
              </div>
            }
          </div>
        }
      }

      <!-- Nothing reaches the board before "Aplicar"; "Limpar" only empties the draft. -->
      @if (chip.def.type !== "daterange") {
        <div
          class="d-flex justify-content-between align-items-center px-2 py-1 border-top"
        >
          <button
            type="button"
            class="btn btn-sm btn-link text-muted text-decoration-none px-0"
            (click)="clearDraft(chip.def)"
          >
            {{ "kanban.filterPanel.clear" | translate }}
          </button>
          <button
            type="button"
            class="btn btn-sm btn-primary"
            (click)="applyDraft(chip.def)"
          >
            {{ "kanban.filterPanel.apply" | translate }}
          </button>
        </div>
      }
    </div>
  }
</ng-template>

<!-- Hidden with d-none, never @if: the chip area must stay in the DOM for its ResizeObserver. -->
<div
  id="kanban-filter-section"
  class="card filter-bar mb-0"
  role="region"
  [class.d-none]="!sectionOpen()"
  [attr.aria-label]="'kanban.filterPanel.filters' | translate"
>
  <!-- The panel's header: what is applied and how many deals it leaves, with the actions on the right. -->
  <div class="card-header d-flex align-items-center gap-2 py-2 px-3">
    <h5 class="header-title mb-0">
      {{ "kanban.filterPanel.appliedTitle" | translate: { count: activeCount() } }}
    </h5>
    @if (shownCount !== null && totalCount !== null) {
      <span class="font-13 text-muted text-nowrap">
        · <b class="text-dark">{{ shownCount }}</b>
        {{ "kanban.filterPanel.ofTotalDeals" | translate: { total: totalCount } }}
      </span>
    }
    <div class="d-flex align-items-center gap-1 flex-shrink-0 ms-auto">
      <div class="position-relative">
        <button
          type="button"
          class="btn btn-sm btn-primary"
          [attr.aria-expanded]="addOpen()"
          (click)="toggleAdd()"
        >
          <i class="mdi mdi-plus"></i>
          {{ "kanban.filterPanel.addFilter" | translate }}
        </button>
        @if (addOpen()) {
          <div class="shadow filter-menu filter-menu-end">
            <div class="filter-menu-scroll filter-menu-scroll-tall">
              @for (group of addMenuGroups(); track group.label) {
                <div class="filter-menu-header">
                  {{ group.label | translate }}
                </div>
                <div class="list-group list-group-flush">
                  @for (def of group.items; track def.key) {
                    <a
                      href="javascript:void(0);"
                      class="list-group-item list-group-item-action p-2 font-12"
                      (click)="addFilter(def)"
                    >
                      {{ def.label | translate }}
                    </a>
                  }
                </div>
              } @empty {
                <div class="p-2 font-12 text-muted">
                  {{
                    (definitions?.length
                      ? "kanban.filterPanel.allApplied"
                      : "kanban.filterPanel.noFiltersAvailable"
                    ) | translate
                  }}
                </div>
              }
            </div>
          </div>
        }
      </div>
      @if (canOfferSave()) {
        <span
          class="d-inline-flex"
          [matTooltip]="
            'kanban.filterPanel.savedFilterLimit'
              | translate: { max: savedLimit() }
          "
          [matTooltipDisabled]="!atSavedLimit()"
        >
          <button
            type="button"
            class="btn btn-sm btn-soft-primary"
            [disabled]="atSavedLimit() || saveOpen()"
            (click)="openSave()"
          >
            <i class="mdi mdi-bookmark-outline"></i>
            {{ "kanban.filterPanel.saveFilter" | translate }}
          </button>
        </span>
      }
      <button
        type="button"
        class="btn btn-sm btn-light"
        [disabled]="chipViews().length === 0"
        (click)="clearFilters()"
      >
        <i class="mdi mdi-filter-remove-outline me-1"></i
        >{{ "kanban.filterPanel.clear" | translate }}
      </button>
    </div>
  </div>

  <div class="card-body p-2 px-3">
    <!--
      The applied filters, on ONE line, never wrapped: as many chips as the width allows
      (`fitChips`), the rest behind "+N". Every chip is its own editor and applies at once.
    -->
    <div class="d-flex align-items-center gap-2">
      <span class="filter-row-label text-uppercase text-muted font-11 fw-bold">
        {{ "kanban.filterPanel.appliedLabel" | translate }}
      </span>

      <div
        #chipsArea
        class="filter-chips flex-grow-1 d-flex align-items-center gap-1"
      >
        @for (chip of visibleChips(); track chip.key; let first = $first) {
          @if (!first) {
            <span class="filter-connector text-muted font-12 fw-bold">{{
              "kanban.filterPanel.and" | translate
            }}</span>
          }
          <div class="filter-chip-slot position-relative">
            <ng-container
              *ngTemplateOutlet="
                chipTemplate;
                context: { $implicit: chip, inMore: false }
              "
            />
          </div>
        }

        @if (overflowChips().length > 0) {
          <div class="position-relative flex-shrink-0">
            <button
              type="button"
              class="btn btn-sm btn-soft-primary"
              [attr.aria-expanded]="moreOpen()"
              [matTooltip]="'kanban.filterPanel.otherApplied' | translate"
              (click)="toggleMore()"
            >
              +{{ overflowChips().length }}
            </button>
            @if (moreOpen()) {
              <div class="shadow filter-menu filter-menu-more">
                <div class="filter-menu-header">
                  {{ "kanban.filterPanel.otherApplied" | translate }}
                </div>
                @for (chip of overflowChips(); track chip.key) {
                  <div class="filter-more-row">
                    <div class="position-relative mw-100">
                      <ng-container
                        *ngTemplateOutlet="
                          chipTemplate;
                          context: { $implicit: chip, inMore: true }
                        "
                      />
                    </div>
                  </div>
                }
              </div>
            }
          </div>
        }

        @if (chipViews().length === 0) {
          <span class="text-muted font-13 text-truncate">{{
            "kanban.filterPanel.noneApplied" | translate
          }}</span>
        }
      </div>
    </div>

    <!-- Saved filters: shown with an applied filter, or while the funnel holds saved ones (RF-01). -->
    @if (activeCount() > 0 || savedFilters().length > 0) {
      <div class="d-flex flex-wrap align-items-center gap-1 mt-2 font-13">
        <span class="filter-row-label text-uppercase text-muted font-11 fw-bold">
          {{ "kanban.filterPanel.savedFilters" | translate }}
        </span>
        @for (saved of savedFilters(); track saved._id) {
          @if (confirmDeleteId() === saved._id) {
            <span class="saved-filter-confirm" role="alertdialog">
              <span>{{
                "kanban.filterPanel.savedFilterDeleteConfirm"
                  | translate: { name: saved.name }
              }}</span>
              <button
                type="button"
                class="btn btn-sm btn-soft-danger"
                (click)="deleteSavedFilter(saved)"
              >
                {{ "kanban.filterPanel.delete" | translate }}
              </button>
              <button
                type="button"
                class="btn btn-sm btn-link text-muted text-decoration-none"
                (click)="confirmDeleteId.set(null)"
              >
                {{ "kanban.filterPanel.cancel" | translate }}
              </button>
            </span>
          } @else {
            <span
              class="saved-filter"
              [class.active]="isSavedActive(saved._id)"
            >
              <button
                type="button"
                class="saved-filter-name"
                [attr.aria-pressed]="isSavedActive(saved._id)"
                [matTooltip]="(isSavedActive(saved._id) ? 'kanban.filterPanel.savedFilterDeselect' : 'kanban.filterPanel.savedFilterApply') | translate"
                (click)="toggleSavedFilter(saved)"
              >
                @if (isSavedActive(saved._id)) {
                  <i class="mdi mdi-check me-1"></i>
                }
                {{ saved.name }}
              </button>
              <button
                type="button"
                class="saved-filter-remove"
                [attr.aria-label]="
                  'kanban.filterPanel.savedFilterDelete' | translate
                "
                (click)="confirmDeleteId.set(saved._id)"
              >
                <i class="mdi mdi-trash-can-outline"></i>
              </button>
            </span>
          }
        } @empty {
          <span class="text-muted">{{
            "kanban.filterPanel.savedFiltersNone" | translate
          }}</span>
        }
        @if (canOfferSave() && saveOpen()) {
          <input
            type="text"
            class="form-control form-control-sm saved-filter-input"
            maxlength="100"
            [formControl]="saveNameControl"
            [placeholder]="'kanban.filterPanel.saveFilterName' | translate"
            [attr.aria-label]="
              'kanban.filterPanel.saveFilterName' | translate
            "
          />
          <button
            type="button"
            class="btn btn-sm btn-primary"
            [disabled]="!saveNameControl.value?.trim() || saving()"
            (click)="saveFilter()"
          >
            {{ "kanban.filterPanel.save" | translate }}
          </button>
          <button
            type="button"
            class="btn btn-sm btn-link text-muted text-decoration-none"
            (click)="cancelSave()"
          >
            {{ "kanban.filterPanel.cancel" | translate }}
          </button>
        }
      </div>
    }
  </div>
</div>

@if (anyMenuOpen()) {
  <!-- Click-catcher that closes every popover. Transparent by design — no dimming. -->
  <div class="filter-backdrop" (click)="closeMenus()"></div>
}
